import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';

const formatDateLabel = (date, granularity) => {
  if (granularity === 'monthly') {
    const d = new Date(date + '-01');
    return d.toLocaleDateString('en-GB', { month: 'short' });
  }
  if (granularity === 'weekly') {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  // daily
  const d = new Date(date);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const CHART_MARGIN = { top: 4, right: 4, left: -20, bottom: 0 };
const AXIS_TICK = { fontSize: 11, fill: '#999' };
const BAR_RADIUS = [4, 4, 0, 0];

const CustomTooltip = ({ active, payload, label, granularity }) => {
  if (!active || !payload?.length) return null;
  const prefix = granularity === 'weekly' ? 'Week of ' : '';
  return (
    <div className="bg-white border border-[#E6E4DD] rounded-lg shadow-lg p-3 text-sm">
      <p className="text-stone-500 text-xs mb-1">{prefix}{formatDateLabel(label, granularity)}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }} className="font-medium">
          {entry.dataKey === 'revenue' ? `£${entry.value.toFixed(0)}` : `${entry.value} bookings`}
        </p>
      ))}
    </div>
  );
};

export function BookingTrendChart({ data, granularity }) {
  if (!data || data.length === 0) {
    return (
      <div className="card-surface p-6" data-testid="booking-trend-chart">
        <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-4">Booking Trends</h3>
        <p className="text-sm text-stone-400 text-center py-8">No booking data for this period.</p>
      </div>
    );
  }

  return (
    <div className="card-surface p-6" data-testid="booking-trend-chart">
      <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-4">Booking Trends</h3>
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E6E4DD" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(d) => formatDateLabel(d, granularity)}
              tick={AXIS_TICK}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={AXIS_TICK}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip granularity={granularity} />} />
            <Bar dataKey="bookings" fill="#D4AF37" radius={BAR_RADIUS} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function RevenueChart({ data, granularity }) {
  if (!data || data.length === 0) {
    return (
      <div className="card-surface p-6" data-testid="revenue-chart">
        <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-4">Revenue</h3>
        <p className="text-sm text-stone-400 text-center py-8">No revenue data for this period.</p>
      </div>
    );
  }

  return (
    <div className="card-surface p-6" data-testid="revenue-chart">
      <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-4">Revenue</h3>
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={CHART_MARGIN}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#059669" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#059669" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E6E4DD" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(d) => formatDateLabel(d, granularity)}
              tick={AXIS_TICK}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(v) => `£${v}`}
              tick={AXIS_TICK}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip granularity={granularity} />} />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#059669"
              strokeWidth={2}
              fill="url(#revenueGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
